import React, { useEffect, useState, useRef } from 'react';
import { IonText } from '@ionic/react';
import SettingsInline from './SettingsInline';
import clsx from 'clsx';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowDownWideShort, faArrowUpShortWide } from '@fortawesome/free-solid-svg-icons';
import { OrderByEnum, TimeFrameValueEnum } from 'common/common';
import { useDispatch } from 'react-redux';
import { SlotCompareReduxCommandCreator } from 'appRedux/actions/slotCompareCommandCreator';
import { useAppSelector } from 'appRedux/createStore';
import { ReactSVG } from 'react-svg';

export const timeframes = [
  { field: TimeFrameValueEnum.months1, label: '30 Days', labelSubTitle: '30 days', prefix: 'm_' },
  { field: TimeFrameValueEnum.weeks1, label: '7 Days', labelSubTitle: '7 days', prefix: 'w_' },
  { field: TimeFrameValueEnum.hours24, label: '24 Hr', labelSubTitle: '24 hrs', prefix: 'd_' },
  { field: TimeFrameValueEnum.hours8, label: '8 Hr', labelSubTitle: '8 hrs', prefix: 'h8_' },
  { field: TimeFrameValueEnum.hours4, label: '4 Hr', labelSubTitle: '4 hrs', prefix: 'h4_' },
  { field: TimeFrameValueEnum.hours1, label: '1 Hr', labelSubTitle: '1 hr', prefix: 'h1_' },
];

const MetricHeader: React.FC = () => {
  const { timeFrameOrderBy, timeFramePrefix, filterActive } = useAppSelector(
    (state) => state.app.persistedState.compareSlotsState
  );

  const dispatch = useDispatch();
  const slotCompareCommands = SlotCompareReduxCommandCreator(dispatch);
  const selectedTimeframe = timeframes.find((tf) => tf.prefix === timeFramePrefix);
  const selectedField = selectedTimeframe ? selectedTimeframe.field : TimeFrameValueEnum.months1;

  const [sortOrder, setSortOrder] = useState<string>(selectedField);
  const [activeSortingDirection, setActiveSortingDirection] = useState<OrderByEnum>(
    timeFrameOrderBy || OrderByEnum.Desc
  );

  const { settingsActive } = useAppSelector((state) => state.app.persistedState.compareSlotsState);
  const filterButton = useRef(null);
  const isSettingsOpen = !!settingsActive;

  const toggleSettings = () =>
    slotCompareCommands.setSettingsActive({ settingsActive: !settingsActive });

  // Proxy funnel icon: once the Insight header scrolls behind the sticky Games
  // column, park a copy of the funnel icon at the Games column's right edge so
  // the insight menu stays reachable. It hides again when the real Insight
  // header scrolls back into view.
  const gameCellRef = useRef<HTMLDivElement>(null);
  const insightCellRef = useRef<HTMLDivElement>(null);
  const [showGameEdgeIcon, setShowGameEdgeIcon] = useState(false);

  useEffect(() => {
    const gameEl = gameCellRef.current;
    const insightEl = insightCellRef.current;
    if (!gameEl || !insightEl) return;

    // Nearest horizontally-scrollable ancestor (the overflow-x:auto wrapper).
    let scrollEl: HTMLElement | null = gameEl.parentElement;
    while (scrollEl && scrollEl.scrollWidth <= scrollEl.clientWidth) {
      scrollEl = scrollEl.parentElement;
    }
    if (!scrollEl) return;

    const update = () => {
      // Games is sticky at left:0, so its right edge marks where content
      // disappears behind it. Show the proxy once the Insight cell's leading
      // edge slides under that boundary.
      const gameRight = gameEl.getBoundingClientRect().right;
      const insightLeft = insightEl.getBoundingClientRect().left;
      setShowGameEdgeIcon(insightLeft < gameRight);
    };

    update();
    scrollEl.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
    return () => {
      scrollEl?.removeEventListener('scroll', update);
      window.removeEventListener('resize', update);
    };
  }, []);

  const getSortIcon = (column: string): JSX.Element | null => {
    if (column === sortOrder) {
      return activeSortingDirection === OrderByEnum.Asc ? (
        <FontAwesomeIcon
          icon={faArrowUpShortWide}
          className="sort-arrow-insights insight-name-sort-icon"
        />
      ) : (
        <FontAwesomeIcon
          icon={faArrowDownWideShort}
          className="sort-arrow-insights insight-name-sort-icon"
        />
      );
    }
    return null;
  };

  const handleSortOrder = (column: string) => {
    if (sortOrder === column) {
      setActiveSortingDirection((prev) =>
        prev === OrderByEnum.Asc ? OrderByEnum.Desc : OrderByEnum.Asc
      );
    } else {
      setSortOrder(column);
      setActiveSortingDirection(OrderByEnum.Desc);
    }
  };

  useEffect(() => {
    const selectedTimeframe = timeframes.find((tf) => tf.field === sortOrder);
    const prefix = selectedTimeframe ? selectedTimeframe.prefix : '';
    slotCompareCommands.setTimeframePrefix({ timeframePrefix: prefix });
    slotCompareCommands.setTimeframe({
      timeframeOrderBy: activeSortingDirection,
      timeframePrefix: prefix,
    });
  }, [sortOrder, activeSortingDirection, slotCompareCommands]);

  return (
    <>
      <div className="compare-grid-row compare-grid-header">
        <div className="cg-cell cg-game" ref={gameCellRef}>
          <IonText color="medium">
            <strong>Games</strong>
          </IonText>
          {showGameEdgeIcon && (
            <div
              className={clsx('filter-icon icon-active insight-menu-toggle cg-game__edge-toggle')}
              onClick={toggleSettings}
            >
              <ReactSVG src="assets/icon/funnel-icon.svg" />
            </div>
          )}
        </div>

        <div />

        <div className="cg-cell cg-insight" ref={insightCellRef}>
          <div
            ref={filterButton}
            className={clsx('filter-icon icon-active insight-menu-toggle')}
            onClick={toggleSettings}
          >
            <ReactSVG src="assets/icon/funnel-icon.svg" />
          </div>
          <IonText className="time-label insight-menu-toggle" onClick={toggleSettings}>
            Stats
          </IonText>
        </div>

        {timeframes.map((tf) => {
          const isActive = tf.field === sortOrder;
          return (
            <React.Fragment key={tf.field}>
              <div />
              <div
                className={clsx('cg-cell cg-tf', isActive && 'timeframe-col--active')}
                onClick={() => handleSortOrder(tf.field)}
              >
                {getSortIcon(tf.field)}
                <IonText className="timeframe__label">{tf.label}</IonText>
              </div>
            </React.Fragment>
          );
        })}
      </div>
      <SettingsInline
        isOpen={isSettingsOpen}
        onClose={() => slotCompareCommands.setSettingsActive({ settingsActive: false })}
      />
    </>
  );
};

export default MetricHeader;
